/**
 * Custom hook for asset management in AnimationStudio
 * Handles fetching, uploading, and organizing assets
 */
import { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import api from '@/config/axiosInstance';

export function useAssets(projectId) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingType, setUploadingType] = useState(null);
  const [assetSearch, setAssetSearch] = useState('');
  const [expandedAssetTypes, setExpandedAssetTypes] = useState({});
  const [deletingAllAssets, setDeletingAllAssets] = useState(false);
  
  // Character profiles
  const [characterProfiles, setCharacterProfiles] = useState([]);
  const [selectedCharacterProfile, setSelectedCharacterProfile] = useState(null);
  
  // Import state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importAssetType, setImportAssetType] = useState(null);
  const [importableProjects, setImportableProjects] = useState([]);
  const [selectedImportProject, setSelectedImportProject] = useState("");
  const [importableAssets, setImportableAssets] = useState([]);
  const [selectedImportAssets, setSelectedImportAssets] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [importing, setImporting] = useState(false);

  // Fetch assets
  const fetchAssets = useCallback(async () => {
    if (!projectId) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/projects/${projectId}/assets`);
      setAssets(response.data || []);
    } catch (error) {
      console.error('Error fetching assets:', error);
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // Fetch character profiles
  const fetchCharacterProfiles = useCallback(async () => {
    if (!projectId) return;
    
    try {
      const response = await api.get(`/projects/${projectId}/characters`);
      setCharacterProfiles(response.data || []);
    } catch (error) {
      console.error('Error fetching character profiles:', error);
    }
  }, [projectId]);

  // Upload asset
  const uploadAsset = useCallback(async (file, type, metadata = {}) => {
    if (!projectId) return null;
    
    setUploadingType(type);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);
      formData.append('name', metadata.name || file.name.replace(/\.[^/.]+$/, ''));
      
      if (metadata.characterProfileId) {
        formData.append('character_profile_id', metadata.characterProfileId);
      }

      const response = await api.post(
        `/projects/${projectId}/assets`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data) {
        setAssets(prev => [...prev, response.data]);
        
        // Show warning if image is too small for animation reference
        if (response.data.size_warning) {
          toast.warning(response.data.size_warning, { duration: 6000 });
        } else {
          toast.success(`${type} uploaded`);
        }
        return response.data;
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(`Failed to upload ${type}`);
      return null;
    } finally {
      setUploadingType(null);
    }
  }, [projectId]);

  // Delete asset
  const deleteAsset = useCallback(async (assetId) => {
    try {
      await api.delete(`/assets/${assetId}`);
      setAssets(prev => prev.filter(a => a.id !== assetId));
      toast.success('Asset deleted');
      return true;
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete asset');
      return false;
    }
  }, []);

  // Delete all assets of a type
  const deleteAllAssetsOfType = useCallback(async (type) => {
    const assetsOfType = assets.filter(a => a.type === type);
    
    if (assetsOfType.length === 0) {
      toast.info(`No ${type} assets to delete`);
      return;
    }

    setDeletingAllAssets(true);
    
    try {
      await Promise.all(
        assetsOfType.map(asset => api.delete(`/assets/${asset.id}`))
      );
      setAssets(prev => prev.filter(a => a.type !== type));
      toast.success(`All ${type} assets deleted`);
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error(`Failed to delete all ${type} assets`);
    } finally {
      setDeletingAllAssets(false);
    }
  }, [assets]);

  // Get assets by type
  const getAssetsByType = useCallback((type) => {
    return assets.filter(a => a.type === type);
  }, [assets]);

  // Get filtered assets
  const getFilteredAssets = useCallback((type) => {
    let filtered = assets.filter(a => a.type === type);
    
    if (assetSearch.trim()) {
      const search = assetSearch.toLowerCase();
      filtered = filtered.filter(a => 
        a.name?.toLowerCase().includes(search) ||
        a.metadata?.tags?.some(t => t.toLowerCase().includes(search))
      );
    }
    
    return filtered;
  }, [assets, assetSearch]);

  // Toggle asset type expansion
  const toggleAssetType = useCallback((type) => {
    setExpandedAssetTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  }, []);

  // Import assets from another project
  const fetchImportableProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      const response = await api.get(`/projects`);
      const projects = (response.data || []).filter(p => p.id !== projectId);
      setImportableProjects(projects);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoadingProjects(false);
    }
  }, [projectId]);

  const fetchImportableAssets = useCallback(async (importProjectId, type) => {
    if (!importProjectId) return;
    
    setLoadingAssets(true);
    try {
      const response = await api.get(`/projects/${importProjectId}/assets`);
      const filtered = (response.data || []).filter(a => a.type === type);
      setImportableAssets(filtered);
    } catch (error) {
      console.error('Error fetching assets:', error);
      toast.error('Failed to load assets');
    } finally {
      setLoadingAssets(false);
    }
  }, []);

  const importAssets = useCallback(async (assetIds) => {
    if (!assetIds?.length || !projectId) return;
    
    setImporting(true);
    try {
      const response = await api.post(`/projects/${projectId}/import-assets`, {
        asset_ids: assetIds
      });
      
      if (response.data?.imported) {
        setAssets(prev => [...prev, ...response.data.imported]);
        toast.success(`Imported ${response.data.imported.length} assets`);
        setShowImportDialog(false);
        setSelectedImportAssets([]);
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import assets');
    } finally {
      setImporting(false);
    }
  }, [projectId]);

  // Initial fetch
  useEffect(() => {
    fetchAssets();
    fetchCharacterProfiles();
  }, [fetchAssets, fetchCharacterProfiles]);

  return {
    // Assets
    assets,
    setAssets,
    loading,
    uploadingType,
    assetSearch,
    setAssetSearch,
    expandedAssetTypes,
    setExpandedAssetTypes,
    deletingAllAssets,
    
    // Character profiles
    characterProfiles,
    setCharacterProfiles,
    selectedCharacterProfile,
    setSelectedCharacterProfile,
    
    // Import state
    showImportDialog,
    setShowImportDialog,
    importAssetType,
    setImportAssetType,
    importableProjects,
    selectedImportProject,
    setSelectedImportProject,
    importableAssets,
    selectedImportAssets,
    setSelectedImportAssets,
    loadingProjects,
    loadingAssets,
    importing,
    
    // Methods
    fetchAssets,
    fetchCharacterProfiles,
    uploadAsset,
    deleteAsset,
    deleteAllAssetsOfType,
    getAssetsByType,
    getFilteredAssets,
    toggleAssetType,
    fetchImportableProjects,
    fetchImportableAssets,
    importAssets
  };
}

export default useAssets;
